import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FC, useEffect, useRef, useState } from 'react';
import { DateToday } from './DateToday';
import { Client } from './Client';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as IconLogout } from './img/iconLogout.svg';
import { ReactComponent as IconCash } from './img/cash.svg';
import { ReactComponent as LockSvg } from './img/lock.svg';
import styles from './Header.module.scss';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import { useDispatch } from 'react-redux';
import { logOut, setBalance, setIsContractSigned, setInfo } from 'redux/auth/slice';
import { authApi } from 'redux/Api/authApi/authApi';
import { useLazyGetInfoUserQuery } from 'redux/Api/profileApi/ProfileApi';

const isUnauthorizedError = (
  error: unknown,
): error is FetchBaseQueryError & { status: number } =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  typeof (error as { status?: unknown }).status === 'number';

export const Header: FC = () => {
  const role = localStorage.getItem('role');
  const token = localStorage.getItem('token');
  const location = useLocation();
  const dispatch = useDispatch();
  const [getUser, { data, error }] = useLazyGetInfoUserQuery();
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showPopap, setShowPopap] = useState(false);

  const logout = () => {
    setShowPopap(false);

    navigate('/login', { replace: true });
    localStorage.removeItem('token');
    localStorage.removeItem('chatToken');
    localStorage.removeItem('role');
    localStorage.removeItem('showCompanySelect');
    dispatch(logOut());

    dispatch(authApi.util.resetApiState());
  };

  useEffect(() => {
    if (role === 'director' && token) getUser();
  }, [location?.pathname]);

  useEffect(() => {
    if (isUnauthorizedError(error) && error.status === 401) {
      return logout();
    }
    if (data?.is_contract_signed) {
      dispatch(setIsContractSigned(data?.is_contract_signed));
    }
    dispatch(
      setBalance({
        balance: data?.balance,
        to_be_paid: data?.to_be_paid,
        infoPercent: {
          commission_min_amount: data?.commission_min_amount,
          commission_percent: data?.commission_percent,
        },
        hold: data?.hold,
        min_order_amount: data?.order_limits?.min,
        max_order_amount: data?.order_limits?.max,
      }),
    );
    dispatch(
      setInfo({
        avatar: data?.avatar,
        name: data?.name,
        fio: data?.fio,
      }),
    );
  }, [data, error]);

  useEffect(() => {
    const clickOutside = (event: MouseEvent) => {
      if (headerRef.current && !event.composedPath().includes(headerRef.current)) {
        setShowPopap(false);
      }
    };
    document.body.addEventListener('click', clickOutside);
    return () => document.body.removeEventListener('click', clickOutside);
  }, []);

  const showMenu = () => {
    setShowPopap(!showPopap);
  };

  if (role === 'director') return null;

  return (
    <div ref={headerRef} className={styles.header}>
      <div className={styles.content}>
        <div className={styles.content_left}>
          <DateToday />
          {role === 'director' && (
            <>
              <Link to={'/profile'} className={styles.content_left_item}>
                <IconCash />
                <p>Баланс</p>
                <p>{data?.balance ? addSpaceNumber(data?.balance) : 0} ₽</p>
              </Link>
              <div className={styles.content_left_item}>
                <LockSvg />
                <p>Зарезервировано</p>
                <p>{data?.hold ? addSpaceNumber(data?.hold) : 0} ₽</p>
              </div>
            </>
          )}
        </div>
        <Client fio={data?.fio || 'WorkSupport'} showMenu={showMenu} />
      </div>
      {showPopap && (
        <div className={styles.popap}>
          <div onClick={logout}>
            <p>Выход</p>
            <IconLogout />
          </div>
        </div>
      )}
    </div>
  );
};
